import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  CircularProgress,
  useTheme,
  alpha,
  Paper,
  IconButton,
  TextField,
  Chip,
} from '@mui/material';
import { useTranslation } from '../hooks/useTranslation';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Category } from '../types/category';
import { Product } from '../types/product';
import { Workshop } from '../types/workshop';
import { Tutorial } from '../types/tutorial';
import { File } from '../types/file';
import api from '../utils/api';
import { useCheckout } from '../contexts/CheckoutContext';
import { useAuth } from '../contexts/AuthContext';
import { ProductType } from '../contexts/CheckoutContext';
import { styled } from '@mui/material/styles';
import { 
  ShoppingCart as ShoppingCartIcon, 
  CalendarToday as CalendarTodayIcon, 
  School as SchoolIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  CheckCircle as CheckCircleIcon,
  GetApp as GetAppIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.spacing(2),
  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'transform 0.2s, box-shadow 0.2s',
  background: '#F0EAE1',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.15)}`,
    background: '#F0EAE1',
  },
}));

const StyledCardMedia = styled(CardMedia)(({ theme }) => ({
  height: 240,
  position: 'relative',
  '&:hover': {
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(to bottom, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.2)})`,
    },
  },
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(3),
  background: `linear-gradient(to bottom, ${alpha(theme.palette.common.white, 0.9)}, ${alpha(theme.palette.common.white, 0.95)})`,
  backdropFilter: 'blur(8px)',
  borderRadius: `0 0 ${theme.spacing(2)} ${theme.spacing(2)}`,
}));

const CategoryTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(2),
  background: '#F0EAE1',
  WebkitBackgroundClip: 'text',
}));

const CategoryDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(4),
  whiteSpace: 'pre-line',
}));

const PriceQuantityContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1.5),
  backgroundColor: alpha(theme.palette.primary.main, 0.05),
  borderRadius: theme.spacing(2),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
}));

const FinalPrice = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.primary.main,
  fontSize: '1.25rem',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const OriginalPrice = styled(Typography)(({ theme }) => ({
  textDecoration: 'line-through',
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
}));

const QuantityControls = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  backgroundColor: theme.palette.common.white,
  padding: theme.spacing(0.5),
  borderRadius: theme.spacing(1),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
}));

const DiscountChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.error.main,
  color: theme.palette.common.white,
  fontWeight: 'bold',
  transform: 'rotate(-5deg)',
}));

const AddToCartButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  textTransform: 'none',
  fontWeight: 'bold',
  padding: theme.spacing(1.5),
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  '&:hover': {
    background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
  },
}));

const MotionGrid = motion(Grid);

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3
    }
  }
};

const cardVariants = {
  hidden: { 
    y: 50,
    opacity: 0,
    scale: 0.8,
    rotate: -5
  },
  visible: {
    y: 0,
    opacity: 1,
    scale: 1,
    rotate: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15
    }
  },
  hover: {
    scale: 1.05,
    rotate: 2,
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 10
    }
  }
};

const SectionHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  marginTop: theme.spacing(6),
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -theme.spacing(2),
    left: 0,
    right: 0,
    height: '2px',
    background: '#F0EAE1',
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  fontSize: '2rem',
  marginBottom: theme.spacing(1),
  WebkitBackgroundClip: 'text',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  '&[dir="rtl"]': {
    flexDirection: 'row-reverse',
    justifyContent: 'flex-end',
    textAlign: 'right',
    '& .MuiSvgIcon-root': {
      marginLeft: 0,
      marginRight: theme.spacing(2)
    }
  },
  '&[dir="ltr"]': {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    textAlign: 'left',
    '& .MuiSvgIcon-root': {
      marginRight: 0,
      marginLeft: theme.spacing(2)
    }
  }
}));

const SectionDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '1.1rem',
  whiteSpace: 'pre-line',
  '&[dir="rtl"]': {
    textAlign: 'right',
    marginRight: 0,
    marginLeft: 'auto',
    paddingRight: theme.spacing(2)
  },
  '&[dir="ltr"]': {
    textAlign: 'left',
    marginLeft: 0,
    marginRight: 'auto',
    paddingLeft: theme.spacing(2)
  }
}));

export const CategoryPage: React.FC = () => {
  const { category } = useParams<{ category: Category }>();
  const { t, currentLang } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { addToCheckout, purchaseHistory, items: checkoutItems, refreshPurchaseHistory } = useCheckout();
  const [products, setProducts] = useState<Product[]>([]);
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [tutorials, setTutorials] = useState<Tutorial[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(true);
  const [quantities, setQuantities] = useState<Record<string, number>>({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [productsRes, workshopsRes, tutorialsRes, filesRes] = await Promise.all([
          api.get<{ data: Product[] }>('/products'),
          api.get<{ data: Workshop[] }>('/workshops'),
          api.get<{ data: Tutorial[] }>('/tutorials'),
          api.get<{ data: File[] }>('/files')
        ]);

        // Filter items by category
        const filteredProducts = productsRes.data.data.filter(product => product.category === category);
        const filteredWorkshops = workshopsRes.data.data.filter(workshop => workshop.category === category);
        const filteredTutorials = tutorialsRes.data.data.filter(tutorial => tutorial.category === category);
        const filteredFiles = filesRes.data.data.filter(file => file.category === category);

        setProducts(filteredProducts);
        setWorkshops(filteredWorkshops);
        setTutorials(filteredTutorials);
        setFiles(filteredFiles);

        // Initialize quantities for filtered products only (not files since they don't use quantities)
        const initialQuantities = filteredProducts.reduce((acc, product) => {
          acc[product._id] = 1;
          return acc;
        }, {} as Record<string, number>);
        setQuantities(initialQuantities);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [category]);

  // Refresh purchase history when component mounts, when user authentication changes, or when navigating to the page
  useEffect(() => {
    if (isAuthenticated) {
      refreshPurchaseHistory();
    }
  }, [isAuthenticated, refreshPurchaseHistory, location.pathname]);

  const handleQuantityChange = (productId: string, value: number) => {
    setQuantities(prev => ({
      ...prev,
      [productId]: Math.max(1, Math.min(value, 999))
    }));
  };

  const isItemPurchased = (itemId: string, productType: ProductType): boolean => {
    return purchaseHistory.some(
      purchase => 
        purchase.item === itemId && 
        purchase.productType === productType && 
        purchase.status === 'completed'
    );
  };

  const isItemInCart = (itemId: string, productType: ProductType): boolean => {
    return checkoutItems.some(
      item => 
        item.item === itemId && 
        item.productType === productType
    );
  };

  const getFinalPrice = (price: number, discount: number): number => {
    return price * (1 - discount / 100);
  };

  const isFileFree = (file: File): boolean => {
    return file.price === 0 || file.discount === 100 || getFinalPrice(file.price, file.discount) === 0;
  };

  const handleAddToCart = async (item: Product | Workshop | Tutorial | File, type: ProductType) => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: location } });
      return;
    }

    try {
      if (type === ProductType.TUTORIAL) {
        const tutorial = item as Tutorial;
        if (tutorial.price === 0 || isItemPurchased(tutorial._id, ProductType.TUTORIAL)) {
          navigate(`/tutorials/${tutorial._id}`);
          return;
        }
      }

      if (type === ProductType.FILE) {
        const file = item as File;
        if (isFileFree(file) || isItemPurchased(file._id, ProductType.FILE)) {
          window.open(file.downloadPath, '_blank');
          return;
        }
      }
      
      if (type === ProductType.ITEM) {
        await addToCheckout(item._id, type, quantities[item._id]);
      } else {
        await addToCheckout(item._id, type);
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  };


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  console.log(currentLang);

  return (
    <Box sx={{ 
      p: { xs: 2, md: 4 },
      direction: currentLang === 'he' ? 'rtl' : 'ltr'
    }}>
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          mb: 4, 
          borderRadius: 3,
          background: '#f7f4f1',
        }}
      >
        <CategoryTitle 
          variant="h4" 
          gutterBottom
          sx={{ 
            textAlign: currentLang === 'he' ? 'right' : 'left',
          }}
        >
          {t(`admin.products.categories.${category}`)}
        </CategoryTitle>
        <CategoryDescription 
          variant="subtitle1"
          sx={{ 
            textAlign: currentLang === 'he' ? 'right' : 'left',
          }}
        >
          {t(`pages.category.${category}.description`)}
        </CategoryDescription>
      </Paper>

      <MotionGrid
        container
        spacing={3}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        direction={currentLang === 'he' ? 'row-reverse' : 'row'}
        sx={{
          justifyContent: currentLang === 'he' ? 'flex-end' : 'flex-start',
          '& > .MuiGrid-item': {
            display: 'flex',
            justifyContent: 'flex-start',
          }
        }}
      >
        <AnimatePresence>
          {tutorials.length > 0 && (
            <>
              <Grid item xs={12}>
                <SectionHeader>
                  <SectionTitle dir={currentLang === 'he' ? 'rtl' : 'ltr'} sx={{ color: theme.palette.primary.main }}>
                    {currentLang === 'he' ? (
                      <>
                        {t('pages.category.tutorials')}
                        <SchoolIcon sx={{ fontSize: '2rem' }} />
                      </>
                    ) : (
                      <>
                        <SchoolIcon sx={{ fontSize: '2rem' }} />
                        {t('pages.category.tutorials')}
                      </>
                    )}
                  </SectionTitle>
                  <SectionDescription dir={currentLang === 'he' ? 'rtl' : 'ltr'}>
                    {t('pages.category.tutorialsDescription')}
                  </SectionDescription>
                </SectionHeader>
              </Grid>
              {tutorials.map((tutorial) => (
                <MotionGrid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={tutorial._id}
                  variants={cardVariants}
                  whileHover="hover"
                  layout
                  sx={{
                    display: 'flex',
                    justifyContent: currentLang === 'he' ? 'flex-end' : 'flex-start',
                  }}
                >
                  <StyledCard sx={{ width: '100%' }}>
                    <StyledCardMedia
                      image={tutorial.imagePath}
                      title={tutorial.title}
                    />
                    <StyledCardContent>
                      <Typography 
                        variant="h6" 
                        gutterBottom
                        sx={{ 
                          textAlign: currentLang === 'he' ? 'right' : 'left',
                          color: theme.palette.primary.main
                        }}
                      >
                        {tutorial.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        paragraph
                        sx={{ 
                          textAlign: currentLang === 'he' ? 'right' : 'left',
                        }}
                      >
                        {tutorial.description}
                      </Typography>
                      <Box sx={{ mt: 'auto', pt: 2 }}>
                        {tutorial.price === 0 || isItemPurchased(tutorial._id, ProductType.TUTORIAL) ? (
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            color: 'success.main',
                            mb: 2
                          }}>
                            <CheckCircleIcon sx={{ mr: 1 }} />
                            <Typography variant="body2">{t('pages.tutorials.available')}</Typography>
                          </Box>
                        ) : (
                          <Box sx={{ mb: 2 }}>
                            {tutorial.discount > 0 ? (
                              <Box sx={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: currentLang === 'he' ? 'flex-end' : 'flex-start',
                                gap: 0.5
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography 
                                    variant="h6" 
                                    color="primary"
                                  >
                                    ₪{Math.round(tutorial.price * (1 - tutorial.discount / 100))}
                                  </Typography>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
                                  >
                                    ₪{tutorial.price}
                                  </Typography>
                                  <Typography 
                                    variant="body2" 
                                    color="error.main"
                                  >
                                    -{tutorial.discount}%
                                  </Typography>
                                </Box>
                              </Box>
                            ) : (
                              <Typography 
                                variant="h6" 
                                color="primary" 
                                gutterBottom
                                sx={{ 
                                  textAlign: currentLang === 'he' ? 'right' : 'left',
                                }}
                              >
                                ₪{tutorial.price}
                              </Typography>
                            )}
                          </Box>
                        )}
                        {tutorial.price === 0 || isItemPurchased(tutorial._id, ProductType.TUTORIAL) ? (
                          <AddToCartButton
                            variant="contained"
                            fullWidth
                            startIcon={currentLang === 'he' ? null : <SchoolIcon />}
                            endIcon={currentLang === 'he' ? <SchoolIcon /> : null}
                            onClick={() => handleAddToCart(tutorial, ProductType.TUTORIAL)}
                          >
                            {t('pages.tutorials.openTutorial')}
                          </AddToCartButton>
                        ) : isItemInCart(tutorial._id, ProductType.TUTORIAL) ? (
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            color: 'success.main',
                            mb: 2
                          }}>
                            <CheckCircleIcon sx={{ mr: 1 }} />
                            <Typography variant="body2">{t('pages.category.inCart')}</Typography>
                          </Box>
                        ) : (
                          <AddToCartButton
                            variant="contained"
                            fullWidth
                            startIcon={currentLang === 'he' ? null : <SchoolIcon />}
                            endIcon={currentLang === 'he' ? <SchoolIcon /> : null}
                            onClick={() => handleAddToCart(tutorial, ProductType.TUTORIAL)}
                          >
                            {t('pages.tutorials.addToCheckout')}
                          </AddToCartButton>
                        )}
                      </Box>
                    </StyledCardContent>
                  </StyledCard>
                </MotionGrid>
              ))}
            </>
          )}

          {files.length > 0 && (
            <>
              <Grid item xs={12}>
                <SectionHeader>
                  <SectionTitle dir={currentLang === 'he' ? 'rtl' : 'ltr'} sx={{ color: theme.palette.primary.main }}>
                    {currentLang === 'he' ? (
                      <>
                        {t('pages.category.files')}
                        <GetAppIcon sx={{ fontSize: '2rem' }} />
                      </>
                    ) : (
                      <>
                        <GetAppIcon sx={{ fontSize: '2rem' }} />
                        {t('pages.category.files')}
                      </>
                    )}
                  </SectionTitle>
                  <SectionDescription dir={currentLang === 'he' ? 'rtl' : 'ltr'}>
                    {t('pages.category.filesDescription')}
                  </SectionDescription>
                </SectionHeader>
              </Grid>
              {files.map((file) => (
                <MotionGrid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                  key={file._id}
                  variants={cardVariants}
                  whileHover="hover"
                  layout
                  sx={{
                    display: 'flex',
                    justifyContent: currentLang === 'he' ? 'flex-end' : 'flex-start',
                  }}
                >
                  <StyledCard sx={{ width: '100%' }}>
                    <StyledCardMedia
                      image={file.imagePath}
                      title={file.title}
                    />
                    <StyledCardContent>
                      <Typography 
                        variant="h6" 
                        gutterBottom
                        sx={{ 
                          textAlign: currentLang === 'he' ? 'right' : 'left',
                          color: theme.palette.primary.main
                        }}
                      >
                        {file.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        paragraph
                        sx={{ 
                          textAlign: currentLang === 'he' ? 'right' : 'left',
                          flexGrow: 1
                        }}
                      >
                        {file.description}
                      </Typography>
                      <Box sx={{ mt: 'auto', pt: 2 }}>
                        {isItemPurchased(file._id, ProductType.FILE) ? (
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            color: 'success.main',
                            mb: 2
                          }}>
                            <CheckCircleIcon sx={{ mr: 1 }} />
                            <Typography variant="body2">זמין</Typography>
                          </Box>
                        ) : (
                          <Typography 
                            variant="h6" 
                            color="primary" 
                            gutterBottom
                            sx={{ 
                              textAlign: currentLang === 'he' ? 'right' : 'left',
                              display: 'flex',
                              alignItems: 'center',
                              gap: theme.spacing(1),
                              justifyContent: currentLang === 'he' ? 'flex-end' : 'flex-start',
                              flexDirection: currentLang === 'he' ? 'row-reverse' : 'row'
                            }}
                          >
                            {file.discount > 0 ? (
                              <>
                                <OriginalPrice>₪{file.price}</OriginalPrice>
                                {getFinalPrice(file.price, file.discount) > 0 && (
                                  <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                                    ₪{Math.round(getFinalPrice(file.price, file.discount))}
                                  </Typography>
                                )}
                                <DiscountChip
                                  label={`${file.discount}% OFF`}
                                  size="small"
                                />
                              </>
                            ) : (
                              file.price === 0 ? null : (
                                `₪${file.price}`
                              )
                            )}
                          </Typography>
                        )}
                        {isFileFree(file) || isItemPurchased(file._id, ProductType.FILE) ? (
                          <AddToCartButton
                            variant="contained"
                            fullWidth
                            startIcon={currentLang === 'he' ? null : <GetAppIcon />}
                            endIcon={currentLang === 'he' ? <GetAppIcon /> : null}
                            onClick={() => handleAddToCart(file, ProductType.FILE)}
                          >
                            {t('pages.category.downloadFile')}
                          </AddToCartButton>
                        ) : isItemInCart(file._id, ProductType.FILE) ? (
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            color: 'success.main',
                            mb: 2
                          }}>
                            <CheckCircleIcon sx={{ mr: 1 }} />
                            <Typography variant="body2">{t('pages.category.inCart')}</Typography>
                          </Box>
                        ) : (
                          <AddToCartButton
                            variant="contained"
                            fullWidth
                            startIcon={currentLang === 'he' ? null : <GetAppIcon />}
                            endIcon={currentLang === 'he' ? <GetAppIcon /> : null}
                            onClick={() => handleAddToCart(file, ProductType.FILE)}
                          >
                            {t('pages.shop.addToCart')}
                          </AddToCartButton>
                        )}
                      </Box>
                    </StyledCardContent>
                  </StyledCard>
                </MotionGrid>
              ))}
            </>
          )}

          {products.length > 0 && (
            <>
              <Grid item xs={12}>
                <SectionHeader>
                  <SectionTitle dir={currentLang === 'he' ? 'rtl' : 'ltr'} sx={{ color: theme.palette.primary.main }}>
                    {currentLang === 'he' ? (
                      <>
                        {t('pages.category.products')}
                        <ShoppingCartIcon sx={{ fontSize: '2rem' }} />
                      </>
                    ) : (
                      <>
                        <ShoppingCartIcon sx={{ fontSize: '2rem' }} />
                        {t('pages.category.products')}
                      </>
                    )}
                  </SectionTitle>
                  <SectionDescription dir={currentLang === 'he' ? 'rtl' : 'ltr'}>
                    {t('pages.category.productsDescription')}
                  </SectionDescription>
                </SectionHeader>
              </Grid>
              {products.map((product) => (
                <MotionGrid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                  key={product._id}
                  variants={cardVariants}
                  whileHover="hover"
                  layout
                  sx={{
                    display: 'flex',
                    justifyContent: currentLang === 'he' ? 'flex-end' : 'flex-start',
                  }}
                >
                  <StyledCard sx={{ width: '100%' }}>
                    <StyledCardMedia
                      image={product.imagePath}
                      title={product.title}
                    />
                    <StyledCardContent>
                      <Typography 
                        variant="h6" 
                        gutterBottom
                        sx={{ 
                          textAlign: currentLang === 'he' ? 'right' : 'left',
                          color: theme.palette.primary.main
                        }}
                      >
                        {product.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        paragraph
                        sx={{ 
                          textAlign: currentLang === 'he' ? 'right' : 'left',
                          flexGrow: 1
                        }}
                      >
                        {product.description}
                      </Typography>
                      <PriceQuantityContainer>
                        <FinalPrice sx={{ 
                          flexDirection: currentLang === 'he' ? 'row-reverse' : 'row',
                          gap: theme.spacing(1)
                        }}>
                          {product.discount > 0 ? (
                            <>
                              <OriginalPrice>₪{product.price}</OriginalPrice>
                              ₪{product.price * (1 - product.discount / 100)}
                              <DiscountChip
                                label={`${product.discount}% OFF`}
                                size="small"
                              />
                            </>
                          ) : (
                            `₪${product.price}`
                          )}
                        </FinalPrice>
                        <QuantityControls>
                          <IconButton
                            size="small"
                            onClick={() => handleQuantityChange(product._id, quantities[product._id] - 1)}
                            sx={{ 
                              color: 'primary.main',
                              '&:hover': { 
                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                transform: 'scale(1.1)',
                              }
                            }}
                          >
                            <RemoveIcon />
                          </IconButton>
                          <TextField
                            size="small"
                            type="number"
                            value={quantities[product._id]}
                            onChange={(e) => handleQuantityChange(product._id, parseInt(e.target.value))}
                            inputProps={{ 
                              min: 1, 
                              max: 999,
                              style: { 
                                textAlign: 'center',
                                padding: '4px 0',
                                width: '40px'
                              }
                            }}
                            sx={{ 
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': { border: 'none' },
                                '&:hover fieldset': { border: 'none' },
                                '&.Mui-focused fieldset': { border: 'none' },
                              }
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleQuantityChange(product._id, quantities[product._id] + 1)}
                            sx={{ 
                              color: 'primary.main',
                              '&:hover': { 
                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                transform: 'scale(1.1)',
                              }
                            }}
                          >
                            <AddIcon />
                          </IconButton>
                        </QuantityControls>
                      </PriceQuantityContainer>
                      <AddToCartButton
                        variant="contained"
                        fullWidth
                        startIcon={currentLang === 'he' ? null : <ShoppingCartIcon />}
                        endIcon={currentLang === 'he' ? <ShoppingCartIcon /> : null}
                        onClick={() => handleAddToCart(product, ProductType.ITEM)}
                      >
                        {t('pages.shop.addToCart')}
                      </AddToCartButton>
                    </StyledCardContent>
                  </StyledCard>
                </MotionGrid>
              ))}
            </>
          )}

          

          {workshops.length > 0 && (
            <>
              <Grid item xs={12}>
                <SectionHeader>
                  <SectionTitle dir={currentLang === 'he' ? 'rtl' : 'ltr'} sx={{ color: theme.palette.primary.main }}>
                    {currentLang === 'he' ? (
                      <>
                        {t('pages.category.workshops')}
                        <CalendarTodayIcon sx={{ fontSize: '2rem' }} />
                      </>
                    ) : (
                      <>
                        <CalendarTodayIcon sx={{ fontSize: '2rem' }} />
                        {t('pages.category.workshops')}
                      </>
                    )}
                  </SectionTitle>
                  <SectionDescription dir={currentLang === 'he' ? 'rtl' : 'ltr'}>
                    {t('pages.category.workshopsDescription')}
                  </SectionDescription>
                </SectionHeader>
              </Grid>
              {workshops.map((workshop) => (
                <MotionGrid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={workshop._id}
                  variants={cardVariants}
                  whileHover="hover"
                  layout
                  sx={{
                    display: 'flex',
                    justifyContent: currentLang === 'he' ? 'flex-end' : 'flex-start',
                  }}
                >
                  <StyledCard sx={{ width: '100%' }}>
                    <StyledCardMedia
                      image={workshop.image}
                      title={workshop.title}
                    />
                    <StyledCardContent>
                      <Typography 
                        variant="h6" 
                        gutterBottom
                        sx={{ 
                          textAlign: currentLang === 'he' ? 'right' : 'left',
                          color: theme.palette.primary.main
                        }}
                      >
                        {workshop.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        paragraph
                        sx={{ 
                          textAlign: currentLang === 'he' ? 'right' : 'left',
                        }}
                      >
                        {workshop.description}
                      </Typography>
                      <Box sx={{ mt: 'auto', pt: 2 }}>
                        {!workshop.shouldHidePrice && (
                          <Typography 
                            variant="h6" 
                            color="primary" 
                            gutterBottom
                            sx={{ 
                              textAlign: currentLang === 'he' ? 'right' : 'left',
                            }}
                          >
                            ₪{workshop.price}
                          </Typography>
                        )}
                        {isItemInCart(workshop._id, ProductType.WORKSHOP) ? (
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            color: 'success.main',
                            mb: 2
                          }}>
                            <CheckCircleIcon sx={{ mr: 1 }} />
                            <Typography variant="body2">{t('pages.category.inCart')}</Typography>
                          </Box>
                        ) : (
                          <AddToCartButton
                            variant="contained"
                            fullWidth
                            startIcon={currentLang === 'he' ? null : <CalendarTodayIcon />}
                            endIcon={currentLang === 'he' ? <CalendarTodayIcon /> : null}
                            onClick={() => handleAddToCart(workshop, ProductType.WORKSHOP)}
                          >
                            {t('pages.workshops.register')}
                          </AddToCartButton>
                        )}
                      </Box>
                    </StyledCardContent>
                  </StyledCard>
                </MotionGrid>
              ))}
            </>
          )}
        </AnimatePresence>
      </MotionGrid>
    </Box>
  );
}; 